import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment, WsmFitmentAxisFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  Button,
  Checkbox,
  Input,
  Select,
  Skeleton,
  Text,
  TrashBinIcon,
} from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import {
  getWsmFitmentErrorMessage,
  getWsmFitmentFieldError,
  getWsmFitmentGeneralErrors,
} from "../errors";
import { schemaMessages } from "../messages";
import { WsmFitmentSchemaFormData } from "../types";
import { wsmFitmentApplicationListUrl, wsmFitmentSchemaListPath } from "../urls";
import { appendPickedAxis, getAxesToAdd, getFitmentLabel, moveAxis } from "../utils";

const FIELDS = ["name", "displayLabel", "isActive", "showAsFacet", "searchableByKeyword", "axes"];

export interface WsmFitmentSchemaDetailsPageProps {
  title: string;
  /** Set on an existing schema: enables Delete and the link to its applications. */
  schemaId?: string;
  /** Every axis the store has: what the add picker offers and what names the rows. */
  axes: WsmFitmentAxisFragment[];
  axesLoading?: boolean;
  initialData: WsmFitmentSchemaFormData;
  errors: WsmErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmFitmentSchemaFormData) => void;
  onDelete?: () => void;
}

export const WsmFitmentSchemaDetailsPage = ({
  title,
  schemaId,
  axes,
  axesLoading,
  initialData,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
  onDelete,
}: WsmFitmentSchemaDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmFitmentSchemaFormData>(initialData);
  const change = (patch: Partial<WsmFitmentSchemaFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const fieldError = (field: string) => getWsmFitmentFieldError(errors, field);
  const helper = (field: string, fallback?: string) =>
    getWsmFitmentErrorMessage(fieldError(field), intl) ?? fallback;
  const generalErrors = getWsmFitmentGeneralErrors(errors, FIELDS);
  const axisById = new Map(axes.map(axis => [axis.id, axis]));
  const axesToAdd = getAxesToAdd(axes, data.axisIds);

  return (
    <DetailPageLayout>
      <TopNav href={wsmFitmentSchemaListPath} title={title}>
        {schemaId && (
          <Button
            variant="secondary"
            onClick={() => navigate(wsmFitmentApplicationListUrl(schemaId))}
            data-test-id="wsm-fitment-schema-applications"
          >
            {intl.formatMessage(schemaMessages.viewApplications)}
          </Button>
        )}
      </TopNav>
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-fitment-schema-general">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(schemaMessages.generalTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="grid" __gridTemplateColumns="1fr 1fr" gap={4}>
              <Input
                label={intl.formatMessage(schemaMessages.name)}
                data-test-id="wsm-fitment-schema-name"
                disabled={disabled}
                value={data.name}
                onChange={event => change({ name: event.target.value })}
                error={!!fieldError("name")}
                helperText={helper("name")}
              />
              <Input
                label={intl.formatMessage(schemaMessages.displayLabel)}
                data-test-id="wsm-fitment-schema-display-label"
                disabled={disabled}
                value={data.displayLabel}
                onChange={event => change({ displayLabel: event.target.value })}
                error={!!fieldError("displayLabel")}
                helperText={helper(
                  "displayLabel",
                  intl.formatMessage(schemaMessages.displayLabelHelp),
                )}
              />
            </Box>
            {generalErrors.map((error, index) => (
              <Text
                key={index}
                size={2}
                color="critical1"
                display="block"
                marginTop={2}
                data-test-id="wsm-fitment-schema-error"
              >
                {getWsmFitmentErrorMessage(error, intl)}
              </Text>
            ))}
          </DashboardCard.Content>
        </DashboardCard>
        <CardSpacer />
        <DashboardCard data-test-id="wsm-fitment-schema-axes">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(schemaMessages.axesTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Text size={2} color="default2">
              {intl.formatMessage(schemaMessages.axesSubtitle)}
            </Text>
            <FormSpacer />
            {axesLoading && <Skeleton />}
            {!axesLoading && data.axisIds.length === 0 && (
              <Text size={3} color="default2" data-test-id="wsm-fitment-schema-axes-empty">
                {intl.formatMessage(schemaMessages.axesEmpty)}
              </Text>
            )}
            <Box display="grid" gap={2}>
              {data.axisIds.map((axisId, index) => {
                const axis = axisById.get(axisId);

                return (
                  <Box
                    key={axisId}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    data-test-id="wsm-fitment-schema-axis-row"
                  >
                    <Text size={3} color="default2" __width="2rem">
                      {index + 1}.
                    </Text>
                    <Text size={3} __flex={1}>
                      {axis ? getFitmentLabel(axis) : <Skeleton />}
                    </Text>
                    <Button
                      variant="tertiary"
                      size="small"
                      icon={<ArrowUpIcon />}
                      title={intl.formatMessage(schemaMessages.moveUp)}
                      disabled={disabled || index === 0}
                      onClick={() => change({ axisIds: moveAxis(data.axisIds, index, -1) })}
                      data-test-id="wsm-fitment-schema-axis-up"
                    />
                    <Button
                      variant="tertiary"
                      size="small"
                      icon={<ArrowDownIcon />}
                      title={intl.formatMessage(schemaMessages.moveDown)}
                      disabled={disabled || index === data.axisIds.length - 1}
                      onClick={() => change({ axisIds: moveAxis(data.axisIds, index, 1) })}
                      data-test-id="wsm-fitment-schema-axis-down"
                    />
                    <Button
                      variant="tertiary"
                      size="small"
                      icon={<TrashBinIcon />}
                      title={intl.formatMessage(schemaMessages.remove)}
                      disabled={disabled}
                      onClick={() => change({ axisIds: data.axisIds.filter(id => id !== axisId) })}
                      data-test-id="wsm-fitment-schema-axis-remove"
                    />
                  </Box>
                );
              })}
            </Box>
            <FormSpacer />
            <Select
              label={intl.formatMessage(schemaMessages.addAxis)}
              data-test-id="wsm-fitment-schema-add-axis"
              disabled={disabled || axesToAdd.length === 0}
              options={axesToAdd.map(axis => ({ value: axis.id, label: getFitmentLabel(axis) }))}
              // Picking appends; the select itself never holds a value.
              value={null}
              // With value={null} macaw passes the whole option, not its value.
              onChange={picked => {
                const axisIds = appendPickedAxis(data.axisIds, picked);

                if (axisIds !== data.axisIds) {
                  change({ axisIds });
                }
              }}
              error={!!fieldError("axes")}
              helperText={helper(
                "axes",
                !axesLoading && axesToAdd.length === 0
                  ? intl.formatMessage(schemaMessages.noAxesLeft)
                  : undefined,
              )}
            />
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.Content>
      <DetailPageLayout.RightSidebar>
        <DashboardCard data-test-id="wsm-fitment-schema-visibility">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(schemaMessages.visibilityTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="grid" gap={4}>
              {(
                [
                  ["isActive", schemaMessages.isActive, schemaMessages.isActiveHelp],
                  ["showAsFacet", schemaMessages.showAsFacet, schemaMessages.showAsFacetHelp],
                  [
                    "searchableByKeyword",
                    schemaMessages.searchableByKeyword,
                    schemaMessages.searchableByKeywordHelp,
                  ],
                ] as const
              ).map(([flag, label, help]) => (
                <Box key={flag}>
                  <Checkbox
                    name={flag}
                    data-test-id={`wsm-fitment-schema-${flag}`}
                    disabled={disabled}
                    checked={data[flag]}
                    onCheckedChange={checked => change({ [flag]: !!checked })}
                  >
                    <Text size={3}>{intl.formatMessage(label)}</Text>
                  </Checkbox>
                  <Text
                    size={2}
                    color={fieldError(flag) ? "critical1" : "default2"}
                    display="block"
                  >
                    {helper(flag, intl.formatMessage(help))}
                  </Text>
                </Box>
              ))}
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.RightSidebar>
      <Savebar>
        {onDelete && <Savebar.DeleteButton onClick={onDelete} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmFitmentSchemaListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-fitment-schema-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmFitmentSchemaDetailsPage;
